import React, { useState, useEffect } from "react";
import MDEditor from "@uiw/react-md-editor";
import axios from "axios";
import { sha256 } from "crypto-hash";
import areObjectsEqual from "../../../../utils/areObjectsEqual";

import how_unique_1 from "../../../../icons/how_unique_1.png";
import how_unique_2 from "../../../../icons/how_unique_2.png";
import how_unique_3 from "../../../../icons/how_unique_3.png";
import how_unique_4 from "../../../../icons/how_unique_4.png";
import { maxFileSizeValidation } from "../../../../utils/common";

// Preview images for default icons
const PREVIEW_IMAGES = {
  1: how_unique_1,
  2: how_unique_2,
  3: how_unique_3,
  4: how_unique_4,
};

// Maximum character lengths
const MAX_LENGTHS = {
  ct_about_main_title: 100,
  ct_about_main_description: 400,
  ct_about_title: 100,
  ct_about_description: 3000,
};

// Initial state for icons
const INITIAL_ICON_STATE = {
  1: { status: false, selected: "", encryptedName: "", preview: "" },
  2: { status: false, selected: "", encryptedName: "", preview: "" },
  3: { status: false, selected: "", encryptedName: "", preview: "" },
  4: { status: false, selected: "", encryptedName: "", preview: "" },
};

/**
 * API service for file operations
 */
const fileAPI = {
  upload: (file, encryptedName) => {
    const formData = new FormData();
    formData.append("file", file, encryptedName);
    return axios.post(`/api/file/upload`, formData);
  },
};

/**
 * Form Component
 * Form for editing campaign "About" section with icons and descriptions
 */
const Form = ({ formData, handleSubmit, imageBuffer }) => {
  const [form, setForm] = useState({});
  const [copyData, setCopyData] = useState();
  const [icons, setIcons] = useState(INITIAL_ICON_STATE);
  const [errors, setErrors] = useState({});
  const [limits, setLimits] = useState({});

  /**
   * Initialize form data when formData prop changes
   */
  useEffect(() => {
    setForm(formData);
    setCopyData(formData);
  }, [formData]);

  // Check if there are unsaved changes
  const hasUnsavedChanges = areObjectsEqual(copyData, form);

  /**
   * Handle text input changes with length validation
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    const maxLength =
      MAX_LENGTHS[
        name.includes("title")
          ? "ct_about_title"
          : name.includes("description")
            ? "ct_about_description"
            : name
      ] || Infinity;

    if (value.length <= maxLength) {
      setForm((prev) => ({ ...prev, [name]: value }));
      setLimits((prev) => ({ ...prev, [name]: value.length === maxLength }));
    }
  };

  /**
   * Handle image file selection and preview
   */
  const onImageChange = (e) => {
    const { name } = e.target;
    const file = e.target.files[0];
    const error = maxFileSizeValidation(file);

    if (!error) {
      setErrors((prev) => ({ ...prev, [name]: false }));
      const extension = file.name.split(".").pop();
      const date = Date.now();

      sha256(`${date}`).then((encryptedName) => {
        const fullName = `${encryptedName}.${extension}`;
        const previewUrl = URL.createObjectURL(file);
        const iconNum = name.split("_").pop();

        setForm((prev) => ({ ...prev, [name]: fullName }));
        setIcons((prev) => ({
          ...prev,
          [iconNum]: {
            status: true,
            selected: file,
            encryptedName: fullName,
            preview: previewUrl,
          },
        }));
      });
    } else {
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  /**
   * Validate all required form fields
   */
  const validateForm = () => {
    const requiredFields = [
      "ct_about_main_title",
      "ct_about_main_description",
      ...Array.from({ length: 4 }, (_, i) => `ct_about_title_${i + 1}`),
      ...Array.from({ length: 4 }, (_, i) => `ct_about_description_${i + 1}`),
    ];

    const newErrors = {};
    requiredFields.forEach((field) => {
      if (!form[field]) newErrors[field] = true;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleClick = async () => {
    if (!validateForm()) return;

    form.ct_check_about_updated = true;

    // Upload all icons in parallel
    await Promise.all(
      Object.entries(icons).map(([num, { selected, encryptedName }]) =>
        selected ? fileAPI.upload(selected, encryptedName) : Promise.resolve()
      )
    );

    handleSubmit(form);
    setCopyData(form);
  };

  /**
   * Render icon section with image upload and text fields
   */
  const renderIconSection = (num) => {
    const iconKey = `ct_about_icon_${num}`;
    const titleKey = `ct_about_title_${num}`;
    const descKey = `ct_about_description_${num}`;
    const { status, preview } = icons[num];

    // Render image preview
    const renderImage = () => {
      if (!form?.[iconKey]) {
        return <img src={PREVIEW_IMAGES[num]} className="custom-choose-file-small" alt="upload" />;
      }

      if (status) {
        return <img src={preview} className="custom-choose-file-small" alt="upload" />;
      }

      // Display saved image from buffer
      try {
        const uint8Array = new Uint8Array(imageBuffer?.[iconKey]?.image?.data);
        let binaryString = "";
        for (let i = 0; i < uint8Array.length; i++) {
          binaryString += String.fromCharCode(uint8Array[i]);
        }
        return (
          <img
            src={`data:${imageBuffer?.[iconKey]?.type};base64,${btoa(binaryString)}`}
            className="custom-choose-file-small"
            alt={`icon${num}`}
          />
        );
      } catch (error) {
        console.error(`Error processing image for ${iconKey}:`, error);
        return <img src={PREVIEW_IMAGES[num]} className="custom-choose-file-small" alt="upload" />;
      }
    };

    return (
      <div className="col-sm-6 mt-4" key={num}>
        <div className="desc-card bg-gray">
          <div className="card-body">
            <div className="upload-wrapper">
              {renderImage()}
              <div>
                <label className="btn btn-upload">
                  Upload
                  <input
                    type="file"
                    name={iconKey}
                    accept="image/*"
                    onChange={onImageChange}
                  />
                </label>
              </div>
              {errors[iconKey] && (
                <span className="form-text text-danger">{errors[iconKey]}</span>
              )}
            </div>

            <h6 className="card-title text-center mt-3">
              <label htmlFor={titleKey} className="form-labels">{`Title #${num}`}</label>
            </h6>
            <textarea
              name={titleKey}
              id={titleKey}
              value={form[titleKey] || ""}
              onChange={handleChange}
              className="form-control mt-2"
              rows="2"
              style={{ resize: "none" }}
              maxLength={MAX_LENGTHS.ct_about_title}
            />
            {errors[titleKey] && (
              <span className="form-text text-danger">{`Title #${num} is Required`}</span>
            )}
            {limits[titleKey] && (
              <span className="form-text text-danger">Maximum character limit reached</span>
            )}

            <h6 className="card-title text-center mt-3">
              <label htmlFor={descKey} className="form-labels">{`Description #${num}`}</label>
            </h6>
            <textarea
              name={descKey}
              id={descKey}
              value={form[descKey] || ""}
              onChange={handleChange}
              className="form-control mt-2"
              rows="5"
              style={{ resize: "none" }}
              maxLength={MAX_LENGTHS.ct_about_description}
            />
            {errors[descKey] && (
              <span className="form-text text-danger">{`Description #${num} is Required`}</span>
            )}
            {limits[descKey] && (
              <span className="form-text text-danger">Maximum character limit reached</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="row ps-4">
      <div className="row col-sm-12 mt-4">
        <div className="col-sm-12">
          <div className="desc-card">
            <h5 className="card-title text-gray form-labels">
              <label htmlFor="ct_about_main_title">Main Title</label>
            </h5>
            <input
              name="ct_about_main_title"
              id="ct_about_main_title"
              value={form.ct_about_main_title || ""}
              onChange={handleChange}
              className="form-control mt-2 inputs"
              style={{ resize: "none" }}
              maxLength={MAX_LENGTHS.ct_about_main_title}
            />
            {errors.ct_about_main_title && (
              <span className="form-text text-danger">
                Main Title is Required
              </span>
            )}
            {limits.ct_about_main_title && (
              <span className="form-text text-danger">
                Maximum character limit reached
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="row col-sm-12 mt-4">
        <div className="col-sm-12">
          <div className="desc-card">
            <h5 className="card-title text-gray form-labels">
              <label htmlFor="ct_about_main_description">
                Main Description
              </label>
            </h5>
            <textarea
              name="ct_about_main_description"
              id="ct_about_main_description"
              value={form.ct_about_main_description || ""}
              onChange={handleChange}
              className="form-control mt-2 inputs"
              rows="5"
              style={{ resize: "none" }}
              maxLength={MAX_LENGTHS.ct_about_main_description}
            />
            {errors.ct_about_main_description && (
              <span className="form-text text-danger">
                Main Description is Required
              </span>
            )}
            {limits.ct_about_main_description && (
              <span className="form-text text-danger">
                Maximum character limit reached
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="col-sm-12 mt-5">
        <div className="row col-sm-12">
          {Array.from({ length: 4 }, (_, i) => renderIconSection(i + 1))}
        </div>
      </div>

      <div className="row col-sm-12 mt-5">
        <div className="col-sm-12">
          <div className="desc-card">
            <h5 className="card-title text-gray">
              <div>FAQS</div>
            </h5>
            <MDEditor
              name="ct_about_faqs"
              id="ct_about_faqs"
              value={form.ct_about_faqs || ""}
              onChange={(value) =>
                setForm((prev) => ({ ...prev, ct_about_faqs: value }))
              }
              className="form-control mt-2"
              height={400}
            />
          </div>
        </div>

        <div className="col-sm-12 mt-5">
          <input
            type="submit"
            value="Save Changes"
            onClick={handleClick}
            className="btn bg-orange form-btn"
            disabled={hasUnsavedChanges}
          />
        </div>
      </div>
    </div>
  );
};

export default Form;
